import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../services/auth/auth.service';
import { useNotification } from '../../../contexts/NotificationContext';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';

const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 минут

export const useLogin = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { error } = useSelector((state: RootState) => state.auth);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    totpCode: '',
    trustDevice: false,
  });
  const [totpRequired, setTotpRequired] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutEnd, setLockoutEnd] = useState<number | null>(null);

  useEffect(() => {
    const storedAttempts = localStorage.getItem('loginAttempts');
    const storedLockoutEnd = localStorage.getItem('lockoutEnd');
    
    if (storedAttempts) {
      setLoginAttempts(parseInt(storedAttempts));
    }
    
    if (storedLockoutEnd) {
      const endTime = parseInt(storedLockoutEnd);
      if (endTime > Date.now()) {
        setIsLocked(true);
        setLockoutEnd(endTime);
      } else {
        localStorage.removeItem('lockoutEnd');
        localStorage.removeItem('loginAttempts');
      }
    }
  }, []);

  useEffect(() => {
    if (isLocked && lockoutEnd) {
      const timer = setInterval(() => {
        if (Date.now() >= lockoutEnd) {
          setIsLocked(false);
          setLockoutEnd(null);
          setLoginAttempts(0);
          localStorage.removeItem('lockoutEnd');
          localStorage.removeItem('loginAttempts');
          clearInterval(timer);
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isLocked, lockoutEnd]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isLocked) {
      const remainingTime = Math.ceil((lockoutEnd! - Date.now()) / 1000 / 60);
      showNotification(`Слишком много попыток. Повторите через ${remainingTime} минут`, 'error');
      return;
    }

    setIsLoading(true);

    try {
      await authService.login(formData.username, formData.password, formData.totpCode, formData.trustDevice);
      localStorage.removeItem('loginAttempts');
      localStorage.removeItem('lockoutEnd');
      navigate('/transactions');
    } catch (err) {
      console.error('Login error:', err);
      if ((err as Error & { code?: string }).code === 'TOTP_REQUIRED') {
        setTotpRequired(true);
        showNotification('Введите код из приложения-аутентификатора', 'info');
        return;
      }
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      localStorage.setItem('loginAttempts', newAttempts.toString());

      if (newAttempts >= MAX_LOGIN_ATTEMPTS) {
        const endTime = Date.now() + LOCKOUT_DURATION;
        setLockoutEnd(endTime);
        setIsLocked(true);
        localStorage.setItem('lockoutEnd', endTime.toString());
        showNotification('Слишком много попыток. Аккаунт заблокирован на 15 минут', 'error');
      } else {
        showNotification(err instanceof Error ? err.message : 'Ошибка авторизации', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getRemainingTime = () => {
    if (!lockoutEnd) return '';
    const minutes = Math.ceil((lockoutEnd - Date.now()) / 1000 / 60);
    return ` (${minutes} мин.)`;
  };

  return {
    formData,
    setFormData,
    isLoading,
    isLocked,
    error,
    handleSubmit,
    getRemainingTime,
    totpRequired,
  };
};
